import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import SellerCard from "./SellerCard";
import InfiniteScroll from "../../component/infiniteScroll/InfiniteScroll";

// 셀러 목록 조회를 위한 GraphQL 쿼리
const GET_GUIDE_COLLECTION_SELLERS = gql`
  query getGuideCollectionSellersV2(
    $guide_collection_id: ID!
    $offset: Int
    $limit: Int
  ) {
    getGuideCollectionSellersV2(
      guide_collection_id: $guide_collection_id
      offset: $offset
      limit: $limit
    ) {
      list {
        id
        rating
        is_certified
        products {
          id
          resizedSmallImages
          title
          category
          brand
          price
        }
        user {
          id
          username
          nickname
          follower_count
          resizedSmallImage
        }
        review_count
      }
      isLast
    }
  }
`;

/**
 * 셀러 목록 컴포넌트
 * 셀러 목록을 표시하고 페이징 처리를 담당합니다.
 * InfiniteScroll 컴포넌트를 활용하여 구현했습니다.
 */
const SellerList = ({ guideCollectionId, limit = 10 }) => {
  // 상태 관리
  const [offset, setOffset] = useState(0);
  const [isLast, setIsLast] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 셀러 목록 조회
  const {
    loading: isLoading,
    fetchMore,
    data,
  } = useQuery(GET_GUIDE_COLLECTION_SELLERS, {
    variables: {
      guide_collection_id: guideCollectionId,
      offset: 0,
      limit,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data && data.getGuideCollectionSellersV2) {
        setIsLast(data.getGuideCollectionSellersV2.isLast || false);
      }
    },
    onError: (err) => {
      console.error("셀러 목록 조회 중 오류 발생:", err);
      setError("셀러 목록을 불러오는 중 오류가 발생했습니다.");
    },
  });

  // 더 불러오기 핸들러
  const handleLoadMore = useCallback(() => {
    if (isLoading || isLast || isLoadingMore) return;

    setIsLoadingMore(true);
    const newOffset = offset + limit;
    setOffset(newOffset);

    fetchMore({
      variables: {
        guide_collection_id: guideCollectionId,
        offset: newOffset,
        limit,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const prevList =
          (prev.getGuideCollectionSellersV2 &&
            prev.getGuideCollectionSellersV2.list) ||
          [];
        const newList =
          (fetchMoreResult.getGuideCollectionSellersV2 &&
            fetchMoreResult.getGuideCollectionSellersV2.list) ||
          [];
        const updatedSellers = [...prevList, ...newList];

        const isLastPage =
          (fetchMoreResult.getGuideCollectionSellersV2 &&
            fetchMoreResult.getGuideCollectionSellersV2.isLast) ||
          false;
        setIsLast(isLastPage);

        return {
          getGuideCollectionSellersV2: {
            ...(fetchMoreResult.getGuideCollectionSellersV2 || {}),
            list: updatedSellers,
          },
        };
      },
    }).finally(() => {
      setIsLoadingMore(false);
    });
  }, [
    guideCollectionId,
    fetchMore,
    isLoading,
    isLast,
    limit,
    offset,
    isLoadingMore,
  ]);

  // 셀러 목록 렌더링
  const renderSellerList = () => {
    if (error) {
      return (
        <div className="sellers-error">
          셀러 정보를 불러오는 중 오류가 발생했습니다.
        </div>
      );
    }

    const sellerList =
      (data &&
        data.getGuideCollectionSellersV2 &&
        data.getGuideCollectionSellersV2.list) ||
      [];

    if (!isLoading && sellerList.length === 0) {
      return <div className="no-sellers">이 컬렉션에 셀러가 없습니다.</div>;
    }

    return (
      <ul className="sellers-grid" role="list">
        {sellerList.map((seller) => (
          <li key={seller.id} role="listitem">
            <SellerCard seller={seller} />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="seller-spotlight-container">
      <InfiniteScroll
        hasMore={!isLast}
        loading={isLoading || isLoadingMore}
        loadMore={handleLoadMore}
      >
        {renderSellerList()}
      </InfiniteScroll>
    </div>
  );
};

SellerList.propTypes = {
  guideCollectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  limit: PropTypes.number,
};

export default SellerList;
